import { lazy, Suspense, useEffect, useState } from 'react';
import { ErrorBoundary } from '@gonfalon/error-boundaries/ErrorBoundary';
import { QueryErrorResetBoundary } from '@tanstack/react-query';

import { CodeEditorFallback } from './CodeEditorFallback';
import type { CodeEditorAdvancedProps } from './internal/CodeEditorAdvanced/CodeEditorAdvanced';
import { loadMode, type ModeLoadingState } from './internal/CodeEditorAdvanced/modes/lazyModeLoader';
import { CodeEditorRetryFallback } from './internal/CodeEditorRetryFallback';

const CodeEditorAdvanced = lazy(async () =>
  import(/* webpackChunkName: "CodeEditorAdvanced" */ './internal/CodeEditorAdvanced/CodeEditorAdvanced').then(
    (module) => ({
      default: module.CodeEditorAdvanced,
    }),
  ),
);

type LazyCodeEditorAdvancedProps = Omit<CodeEditorAdvancedProps, 'modeConfig'> & {
  fullscreen?: boolean;
};

const ModeLoader = (props: LazyCodeEditorAdvancedProps) => {
  const { mode = 'json', fullscreen = true } = props;

  const [modeLoadingState, setModeLoadingState] = useState<ModeLoadingState>({
    isLoading: true,
    mode: undefined,
  });
  const [loadRequest, setLoadRequest] = useState({ mode, nonce: 0 });
  if (loadRequest.mode !== mode) {
    setLoadRequest({ mode, nonce: 0 });
    setModeLoadingState({ isLoading: true, mode: undefined });
  }

  useEffect(() => {
    let cancelled = false;
    const modeToLoad = loadRequest.mode;

    void (async () => {
      try {
        const modeConfig = await loadMode(modeToLoad);
        if (!cancelled) {
          setModeLoadingState({
            isLoading: false,
            mode: modeConfig,
          });
        }
      } catch (error) {
        if (!cancelled) {
          setModeLoadingState({
            isLoading: false,
            error: error instanceof Error ? error : new Error('Failed to load mode'),
            mode: undefined,
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loadRequest]);

  const retryLoad = () => {
    setModeLoadingState({ isLoading: true, mode: undefined });
    setLoadRequest((prev) => ({ ...prev, nonce: prev.nonce + 1 }));
  };

  if (modeLoadingState.isLoading) {
    return <CodeEditorFallback fullscreen={fullscreen} />;
  }

  if (modeLoadingState.error) {
    return <CodeEditorRetryFallback resetErrorBoundary={retryLoad} />;
  }

  if (modeLoadingState.mode) {
    return (
      <Suspense fallback={<CodeEditorFallback fullscreen={fullscreen} />}>
        <CodeEditorAdvanced {...(props as CodeEditorAdvancedProps)} modeConfig={modeLoadingState.mode} />
      </Suspense>
    );
  }

  return <CodeEditorFallback fullscreen={fullscreen} />;
};

export const LazyCodeEditorAdvanced = (props: LazyCodeEditorAdvancedProps) => (
  <QueryErrorResetBoundary>
    {({ reset }) => (
      <ErrorBoundary
        severity="medium"
        onReset={reset}
        fallbackRender={({ resetErrorBoundary }) => <CodeEditorRetryFallback resetErrorBoundary={resetErrorBoundary} />}
      >
        <ModeLoader {...props} />
      </ErrorBoundary>
    )}
  </QueryErrorResetBoundary>
);
